/* eslint-disable jsx-a11y/anchor-is-valid */
// TDSDashboard.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Card, Tabs, Typography, Button, Tag, Space, Modal, Form, Select, InputNumber, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { DollarCircleOutlined, MoneyCollectOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../core/data/redux/store';
import { fetchTdsDeclarations } from '../../../core/data/redux/payroll/TDS/tdsDelcaration/tdsDeclarationAction';
import { updateTdsDeclaration } from '../../../core/data/redux/payroll/TDS/tdsDelcaration/tdsDeclarationAction';
import Table from "../../../core/common/dataTable/index";
import { filterByStatus } from '../../../core/utils/filterActiveData';
import { getEmployees } from '../../../core/data/redux/employee/employeeAction';

const { Title } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const STATUS_OPTIONS = [
    { label: 'Pending', value: 'pending' },
    { label: 'Approved', value: 'approved' },
    { label: 'Rejected', value: 'rejected' },
] as const;

const statusColor: Record<string, string> = {
    pending: 'orange',
    approved: 'green',
    partially_approved: 'geekblue',
    rejected: 'red',
};

const currencyParser = (displayValue?: string) => {
    if (displayValue == null) return 0;
    const n = Number(String(displayValue).replace(/[₹,\s]/g, ''));
    return Number.isFinite(n) ? n : 0;
};

const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];

// Helpers
const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];
const monthNameToIndex = (m: string) => MONTHS.indexOf(m);

const TDSAdminDashboardNew: React.FC = () => {
    const [activeTab, setActiveTab] = useState('dashboard');
    const { declarations = [] } = useSelector((state: any) => state.TDS.tdsDeclaration);
    const dispatch = useDispatch<AppDispatch>();

    // Employees
    const { employees } = useSelector((state: any) => state.employee);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const activeEmployeeData = filterByStatus(employees, 'active', 'employee_status') || [];

    // Filter states (default to current month/year)
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = MONTHS[today.getMonth()];
    const years = useMemo(() => Array.from({ length: 7 }, (_, idx) => currentYear - idx), [currentYear]);

    const [selectedYear, setSelectedYear] = useState<number | 'All'>(currentYear);
    const [selectedMonth, setSelectedMonth] = useState<string>(currentMonth);
    const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<number[]>([]);
    const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]); // NEW

    // rerender key for your custom Table
    const [tableKey, setTableKey] = useState(0);

    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [currentRecord, setCurrentRecord] = useState<any | null>(null);
    const [form] = Form.useForm();

    useEffect(() => {
        dispatch(fetchTdsDeclarations());
    }, [dispatch]);

    useEffect(() => {
        dispatch(getEmployees());
    }, [dispatch]);

    useEffect(() => {
        setTableKey((k) => k + 1);
    }, [declarations, selectedMonth, selectedYear, selectedEmployeeIds, selectedStatuses]);

    // Derived: employee options for Select
    const employeeOptions = useMemo(
        () =>
            (activeEmployeeData || []).map((e: any) => ({
                label: e?.Name || `Emp ${e?.EmployeeID}`,
                value: Number(e?.EmployeeID),
            })),
        [activeEmployeeData]
    );

    // Filtering logic (year/month by DeclarationDate, employees by EmployeeID, status by VerificationStatus)
    const filteredDeclarations = useMemo(() => {
        return (declarations || []).filter((d: any) => {
            const dt = dayjs(d?.DeclarationDate);
            const passYear = selectedYear === 'All' ? true : dt.year() === Number(selectedYear);
            const passMonth = selectedMonth === 'All' ? true : dt.month() === monthNameToIndex(selectedMonth);
            const passEmp = selectedEmployeeIds.length === 0 ? true : selectedEmployeeIds.includes(Number(d?.EmployeeID));
            const normalizedStatus = (d?.VerificationStatus || '').toLowerCase();
            const passStatus = selectedStatuses.length === 0 ? true : selectedStatuses.includes(normalizedStatus);
            return passYear && passMonth && passEmp && passStatus;
        });
    }, [declarations, selectedYear, selectedMonth, selectedEmployeeIds, selectedStatuses]);

    const clearFilters = () => {
        setSelectedEmployeeIds([]);
        setSelectedStatuses([]);
    };

    const openStatusModal = (record: any) => {
        setCurrentRecord(record);
        const declared = parseFloat(record?.DeclaredAmount || '0') || 0;
        const verified = parseFloat(record?.VerifiedAmount || '0');
        form.setFieldsValue({
            VerificationStatus: record?.VerificationStatus || 'pending',
            VerifiedAmount: Number.isFinite(verified) && verified > 0 ? verified : declared,
        });
        setStatusModalOpen(true);
    };

    const closeStatusModal = () => {
        setStatusModalOpen(false);
        setCurrentRecord(null);
        form.resetFields();
    };

    const handleStatusSubmit = async () => {
        try {
            const values = await form.validateFields();
            if (!currentRecord?.DeclarationID) {
                message.error('Invalid declaration record.');
                return;
            }
            await dispatch(
                updateTdsDeclaration({
                    id: currentRecord.DeclarationID,
                    VerificationStatus: values.VerificationStatus,
                    VerifiedAmount: values.VerifiedAmount,
                })
            ).unwrap();

            message.success('Verification updated successfully.');
            closeStatusModal();
            dispatch(fetchTdsDeclarations());
        } catch (err: any) {
            if (err?.message) message.error(err.message);
        }
    };

    const declarationColumns: ColumnsType<any> = [
        { title: 'Employee', dataIndex: 'EmployeeName', key: 'EmployeeName' },
        { title: 'Section', dataIndex: 'SectionCode', key: 'SectionCode' },
        {
            title: 'Sub Type',
            dataIndex: 'SubType',
            key: 'SubType',
            render: (text: string) => (text === 'N/A' ? '—' : text),
            filters: [
                { text: 'N/A', value: 'N/A' },
                { text: 'Self/Family', value: 'Self/Family' },
                { text: 'Parents', value: 'Parents' },
            ],
            onFilter: (v, r: any) => r.SubType === v,
        },
        { title: 'Financial Year', dataIndex: 'FinancialYearName', key: 'FinancialYearName' },
        {
            title: 'Declaration Date',
            dataIndex: 'DeclarationDate',
            key: 'DeclarationDate',
            render: (text: string) => dayjs(text).format('DD MMM YYYY'),
            sorter: (a: any, b: any) =>
                dayjs(a.DeclarationDate).valueOf() - dayjs(b.DeclarationDate).valueOf(),
        },
        {
            title: 'Declared Amount',
            dataIndex: 'DeclaredAmount',
            key: 'DeclaredAmount',
            render: (amount: string) => `${parseFloat(amount || '0').toLocaleString('en-IN')}`,
            sorter: (a: any, b: any) =>
                parseFloat(a.DeclaredAmount || '0') - parseFloat(b.DeclaredAmount || '0'),
            align: 'right',
        },
        {
            title: 'Verified Amount',
            dataIndex: 'VerifiedAmount',
            key: 'VerifiedAmount',
            render: (amount: string) => `${parseFloat(amount || '0').toLocaleString('en-IN')}`,
            sorter: (a: any, b: any) =>
                parseFloat(a.VerifiedAmount || '0') - parseFloat(b.VerifiedAmount || '0'),
            align: 'right',
        },
        {
            title: 'Proof Submitted',
            dataIndex: 'ProofStatus',
            key: 'ProofStatus',
            render: (status: string) => {
                const color = status === 'submitted' ? 'green' : 'orange';
                return <Tag color={color}>{(status || '').toUpperCase()}</Tag>;
            },
            filters: [
                { text: 'Submitted', value: 'submitted' },
                { text: 'Not Submitted', value: 'not_submitted' },
            ],
            onFilter: (value, record) => record.ProofStatus === value,
        },
        {
            title: 'Verification',
            key: 'VerificationStatus',
            render: (_: any, record: any) => (
                <Space size="small">
                    <Tag color={statusColor[record?.VerificationStatus || 'pending'] || 'default'}>
                        {(record?.VerificationStatus || 'pending').toUpperCase()}
                    </Tag>
                    <Button size="small" onClick={() => openStatusModal(record)}>
                        Change
                    </Button>
                </Space>
            ),
            filters: STATUS_OPTIONS.map(s => ({ text: s.label, value: s.value })),
            onFilter: (v, r: any) => r.VerificationStatus === v,
            sorter: (a: any, b: any) =>
                (a.VerificationStatus || '').localeCompare(b.VerificationStatus || ''),
        },
        {
            title: 'File',
            dataIndex: 'FilePath',
            key: 'FilePath',
            render: (filePath: string) => {
                if (!filePath) return <span style={{ color: '#999' }}>No file</span>;
                const ext = filePath?.split('.').pop()?.toLowerCase();
                const isImage = imageExtensions.includes(ext || '');
                return (
                    <a
                        href={filePath}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-primary"
                        title={isImage ? 'View Image' : 'Download File'}
                    >
                        <i className={`ti ${isImage ? 'ti-eye' : 'ti-download'}`} />
                    </a>
                );
            },
        },
    ];

    return (
        <div className="page-wrapper">
            <div className="content">
                <div className="tds-admin-dashboard">
                    <div className="bg-white shadow-sm border-bottom mb-4">
                        <div className="container-fluid">
                            <div className="row align-items-center py-3">
                                <div className="col">
                                    <Title level={3} className="mb-0">
                                        <MoneyCollectOutlined className="me-2 text-primary" />
                                        Dashboard
                                    </Title>
                                </div>

                                {/* Filters */}
                                <div className="col-auto ms-auto d-flex align-items-center gap-2 flex-wrap">

                                    {/* Year Filter */}
                                    <Select
                                        style={{ minWidth: 140 }}
                                        value={selectedYear}
                                        onChange={(v) => setSelectedYear(v)}
                                        placeholder="Year"
                                        allowClear={false}
                                        options={[
                                            { label: 'All Years', value: 'All' as const },
                                            ...years.map((y) => ({ label: String(y), value: y }))
                                        ]}
                                    />

                                    {/* Month Filter */}
                                    <Select
                                        style={{ minWidth: 160 }}
                                        value={selectedMonth}
                                        onChange={(v) => setSelectedMonth(v)}
                                        placeholder="Month"
                                        allowClear={false}
                                        options={[
                                            { label: 'All Months', value: 'All' },
                                            ...MONTHS.map((m) => ({ label: m, value: m }))
                                        ]}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="container-fluid">
                        <Tabs activeKey={activeTab} onChange={setActiveTab} size="large" className="mb-4">
                            <TabPane
                                tab={<span><DollarCircleOutlined className='me-1' />Declaration Requests</span>}
                                key="dashboard"
                            >
                                <Card>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <Title level={4} className="mb-0">
                                            Declaration Requests
                                            <span style={{ fontSize: 14, marginLeft: 8, color: '#888' }}>
                                                ({filteredDeclarations.length} of {declarations.length})
                                            </span>
                                        </Title>

                                        <div className="d-flex align-items-center ms-auto gap-2 flex-wrap">
                                            {/* Employee Filter (multi) */}
                                            <Select
                                                mode="multiple"
                                                allowClear
                                                showSearch
                                                optionFilterProp="label"
                                                maxTagCount="responsive"
                                                style={{ minWidth: 260 }}
                                                value={selectedEmployeeIds}
                                                onChange={(vals) => setSelectedEmployeeIds(vals as number[])}
                                                placeholder="Filter by employees"
                                                options={employeeOptions}
                                                className='custom-select'
                                            />

                                            {/* Status Filter (multi) */}
                                            <Select
                                                mode="multiple"
                                                allowClear
                                                style={{ minWidth: 220 }}
                                                value={selectedStatuses}
                                                onChange={(vals) => setSelectedStatuses(vals as string[])}
                                                placeholder="Filter by status"
                                                options={STATUS_OPTIONS.map(s => ({ label: s.label, value: s.value }))}
                                                className='custom-select'
                                            />

                                            <Button onClick={clearFilters}>Clear</Button>
                                        </div>
                                    </div>

                                    <Table
                                        key={tableKey}
                                        dataSource={filteredDeclarations}
                                        columns={declarationColumns}
                                        Selection={true}
                                    />
                                </Card>
                            </TabPane>
                        </Tabs>
                    </div>
                </div>
            </div>

            {/* Verification Status Modal */}
            <Modal
                title="Update Verification"
                open={statusModalOpen}
                onOk={handleStatusSubmit}
                onCancel={closeStatusModal}
                okText="Update"
                destroyOnClose
            >
                <Form form={form} layout="vertical" preserve={false}>
                    <Form.Item
                        label="Verification Status"
                        name="VerificationStatus"
                        rules={[{ required: true, message: 'Please select a status' }]}
                    >
                        <Select placeholder="Select status">
                            {STATUS_OPTIONS.map(opt => (
                                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Verified Amount"
                        name="VerifiedAmount"
                        rules={[
                            { required: true, message: 'Please enter verified amount' },
                            { type: 'number', min: 0, message: 'Amount must be >= 0' },
                        ]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            step={1}
                            formatter={(v) =>
                                v === undefined || v === null ? '' : `₹ ${Number(v).toLocaleString('en-IN')}`
                            }
                            parser={currencyParser}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default TDSAdminDashboardNew;
